import { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { TemplateClassNames } from '../../../types/template-layouts';
import { SingleSignOnTypes } from '../../../types/working-model';
import { SingleSignOnIcon } from '../../admin/create/homepage/navigation/panel/settings/registration-settings/single-sign-on';
import { Button } from '../../general-ui/button/button';
import { EIcon } from '../../general-ui/icon/icon';

enum RegFailureReasons {
	passcode = 'invalid-passcode',
	domain = 'invalid-email-domain',
	missingRequired = 'missing-required-field',
	missingPasscodeLists = 'no-matching-passcode-lists',
	notFoundInPasscodeLists = 'not-found-passcode-lists'
}

export interface RegistrationFooterProps {
	isNextDisabled?: boolean;
	isFirstStep: boolean;
	isFinalStep: boolean;
	template?: TemplateClassNames;
	onPrevStepClick: (e: any) => void;
	singleSignOns: SingleSignOnTypes[];
	waiting: boolean;
	error?: string;
	notSkippable?: boolean;
}
export const RegistrationFooter = ({
	isNextDisabled,
	isFirstStep,
	isFinalStep,
	template,
	onPrevStepClick,
	singleSignOns,
	waiting,
	error = '',
	notSkippable,
}: RegistrationFooterProps): JSX.Element => {
	const { t } = useTranslation("homepage");

	const regError = useMemo(() => {
		if (!error) return '';

		switch (error) {
			case RegFailureReasons.domain: {
				return t("registration.domain");
			}
			// this should never fire because submission is (should be) blocked until required fields are completed
			case RegFailureReasons.missingRequired: {
				return t("registration.missingRequired");
			}
			case RegFailureReasons.notFoundInPasscodeLists: {
				return t("registration.notFoundInPasscodeLists");
			}
			case RegFailureReasons.passcode: {
				return t("registration.passcode");
			}
			default: {
				return t("registration.error");
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	if (isFirstStep)
		return (
			<div className="registration-actions primary">
				{error && <p className="registration-error">{regError}</p>}
				<Button
					classButton="primary full-width"
					template={template}
					typeBtn="submit"
					isDisabled={isNextDisabled || waiting}
				>
					{t("Next")}
				</Button>
				<div className="button-icon-wrapper">
					{singleSignOns?.map((signOn) => {
						return (
							<Button
								key={signOn}
								isRound={true}
								classButton={classNames('secondary', signOn)}
								template={template}
								typeBtn="button"
								aria-label={signOn}
							>
								<SingleSignOnIcon type={signOn} />
							</Button>
						);
					})}
				</div>
			</div>
		);
	else if (isFinalStep)
		return (
			<div className="registration-actions secondary">
				{error && <p className="registration-error">{regError}</p>}
				<Button
					isRound={true}
					classButton={classNames('registration-button secondary')}
					template={template}
					onClick={onPrevStepClick}
					typeBtn="button"
				>
					<EIcon name="keyboard-arrow-left" />
				</Button>

				<div>
					{!notSkippable && (<Button
						classButton={classNames('registration-button secondary')}
						template={template}
						typeBtn="submit"
						isDisabled={waiting}
					>
						{t("Skip")}
					</Button>)}
					<Button
						classButton={classNames('registration-button primary')}
						template={template}
						typeBtn="submit"
						isDisabled={waiting}
					>
						{t("Submit")}
					</Button>
				</div>
			</div>
		);
	return (
		<div className="registration-actions secondary">
			{error && <p className="registration-error">{regError}</p>}
			<Button
				isRound={true}
				classButton={classNames('registration-button secondary')}
				template={template}
				onClick={onPrevStepClick}
			>
				<EIcon name="keyboard-arrow-left" />
			</Button>
			<div>
				{!notSkippable && (<Button
					classButton={classNames('registration-button secondary')}
					template={template}
					isDisabled={waiting}
					typeBtn="submit"
				>
					{t("Skip")}
				</Button>)}
				<Button
					classButton={classNames('registration-button primary')}
					template={template}
					isDisabled={waiting}
					typeBtn="submit"
				>
					{t("Next")}
				</Button>
			</div>
		</div>
	);
};
